import { Alert, Button, Form, Input } from "antd";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api } from "../app/api.ts";
import { applyProblemToForm, requestFailure, type RequestFailure } from "../app/form-errors.ts";
import { AuthCard } from "../components/AuthCard.tsx";
import { rateLimitedMessage } from "../app/retry-after.ts";
import type { LoginLocationState } from "./LoginPage.tsx";

/** Mirrors CreateUser.MinPasswordLength on the server, which stays the authority. */
const MIN_PASSWORD_LENGTH = 8;

interface ResetForm {
  newPassword: string;
  confirmPassword: string;
}

const formFields = ["newPassword"] as const;

/** Step 2 of the password reset: the emailed link opens this screen with ?token=…; on success the user logs in again. */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [form] = Form.useForm<ResetForm>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: async (values: ResetForm) => {
      const { error, response } = await api.POST("/auth/reset-password", { body: { token, newPassword: values.newPassword } });
      if (!response.ok) throw requestFailure(error, response.status, response.headers);
    },
    onSuccess: () => navigate("/login", { replace: true, state: { passwordReset: true } satisfies LoginLocationState }),
    onError: (failure: RequestFailure) =>
      setServerErrors(
        failure.status === 429
          ? [rateLimitedMessage(t, failure.retryAfterSeconds)]
          : applyProblemToForm(failure, form, formFields, t("app.unavailable")),
      ),
  });

  return (
    <AuthCard subtitle={t("auth.reset.title")}>
      {token === "" ? (
        <Alert type="error" showIcon title={t("auth.reset.missingToken")} role="alert" />
      ) : (
        <>
          {serverErrors.length > 0 && (
            <Alert type="error" showIcon style={{ marginBottom: 16 }} role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />
          )}
          <Form<ResetForm> form={form} layout="vertical" requiredMark={false} onFinish={(values) => mutation.mutate(values)}>
            <Form.Item
              name="newPassword"
              label={t("auth.reset.newPassword")}
              rules={[
                { required: true, message: t("auth.required") },
                { min: MIN_PASSWORD_LENGTH, message: t("auth.reset.passwordMin", { min: MIN_PASSWORD_LENGTH }) },
              ]}
            >
              <Input.Password autoComplete="new-password" autoFocus size="large" />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label={t("auth.reset.confirmPassword")}
              dependencies={["newPassword"]}
              rules={[
                { required: true, message: t("auth.required") },
                ({ getFieldValue }) => ({
                  validator: (_, value: string | undefined) =>
                    !value || value === getFieldValue("newPassword") ? Promise.resolve() : Promise.reject(new Error(t("auth.reset.passwordMismatch"))),
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" size="large" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={mutation.isPending}>
              {t("auth.reset.submit")}
            </Button>
          </Form>
        </>
      )}
      <div style={{ textAlign: "center", marginTop: 16, display: "grid", gap: 8 }}>
        <Link to="/forgot-password">{t("auth.reset.requestNewLink")}</Link>
        <Link to="/login">{t("auth.backToLogin")}</Link>
      </div>
    </AuthCard>
  );
}
